import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Form, SearchInput, toast } from '@qodo/design-system';
import { Building2, ChevronDown, ChevronRight, Database } from 'lucide-react';
import { useBitbucketInstallWebhooksMutation } from 'network/mutations/useBitbucketInstallWebhooksMutation';
import { useBitbucketWizardSelectionOptionsQuery } from 'network/queries/useBitbucketWizardSelectionOptionsQuery';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { Checkbox } from 'ui/checkbox';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { useBitbucketInstallation } from '../bitbucketInstallationContext';
import { FORM_IDS } from '../consts';

const spawnSelect = spawn('dialogs.bitbucket-installation.select-repositories');
const spawnErrors = spawn('dialogs.bitbucket-installation.errors');

export const SelectRepositories = () => {
  const { setIsNextEnabled, setIsPending, goNext } = useMultiStepDialog();
  const {
    repositories,
    setRepositories,
    selectedWorkspace,
    bitbucketWorkspaceSlug,
    selectedProviderRepoIds,
    setSelectedProviderRepoIds,
    setWebhookResults,
  } = useBitbucketInstallation();

  const [searchQuery, setSearchQuery] = useState('');
  const [isWorkspaceExpanded, setIsWorkspaceExpanded] = useState(true);

  const methods = useForm();
  const { mutate: installWebhooks } = useBitbucketInstallWebhooksMutation();
  const { data: availableData, isLoading: isLoadingRepos } =
    useBitbucketWizardSelectionOptionsQuery(bitbucketWorkspaceSlug);

  useEffect(() => {
    if (availableData?.items) {
      setRepositories(availableData.items);
    }
  }, [availableData, setRepositories]);

  const workspaceName = selectedWorkspace?.name ?? '';

  useEffect(() => {
    setIsNextEnabled(selectedProviderRepoIds.length > 0);
  }, [selectedProviderRepoIds, setIsNextEnabled]);

  const filteredRepositories = useMemo(() => {
    if (!searchQuery.trim()) return repositories;

    const query = searchQuery.toLowerCase();

    return repositories.filter((repo) =>
      repo.name.toLowerCase().includes(query),
    );
  }, [repositories, searchQuery]);

  const allFilteredIds = filteredRepositories.map((r) => r.providerRepoId);

  const isWorkspaceSelected =
    allFilteredIds.length > 0 &&
    allFilteredIds.every((id) => selectedProviderRepoIds.includes(id));

  const isWorkspaceIndeterminate =
    !isWorkspaceSelected &&
    allFilteredIds.some((id) => selectedProviderRepoIds.includes(id));

  const onWorkspaceToggle = () => {
    if (isWorkspaceSelected) {
      setSelectedProviderRepoIds(
        selectedProviderRepoIds.filter((id) => !allFilteredIds.includes(id)),
      );
    } else {
      setSelectedProviderRepoIds([
        ...selectedProviderRepoIds,
        ...allFilteredIds.filter((id) => !selectedProviderRepoIds.includes(id)),
      ]);
    }
  };

  const onRepositoryToggle = (providerRepoId: string) => {
    const isSelected = selectedProviderRepoIds.includes(providerRepoId);

    if (isSelected) {
      setSelectedProviderRepoIds(
        selectedProviderRepoIds.filter((id) => id !== providerRepoId),
      );
    } else {
      setSelectedProviderRepoIds([...selectedProviderRepoIds, providerRepoId]);
    }
  };

  const onSetupRepositories = () => {
    if (!bitbucketWorkspaceSlug || selectedProviderRepoIds.length === 0) return;

    setIsPending(true);

    installWebhooks(
      {
        bitbucketWorkspaceSlug,
        providerRepoIds: selectedProviderRepoIds,
      },
      {
        onSuccess: (data) => {
          setWebhookResults(data.results);
          setIsPending(false);
          goNext();
        },
        onError: () => {
          toast.error(spawnErrors('webhook-install-failed'));
          setIsPending(false);
        },
      },
    );
  };

  const workspaceCheckState = isWorkspaceIndeterminate
    ? 'indeterminate'
    : isWorkspaceSelected;
  const ChevronIcon = isWorkspaceExpanded ? ChevronDown : ChevronRight;

  return (
    <Step title={spawnSelect('title')} description={spawnSelect('description')}>
      <Form
        {...({ id: FORM_IDS.SELECT_REPOSITORIES } as Record<string, string>)}
        methods={methods}
        onSubmit={onSetupRepositories}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-bg-neutral-mid">
              {spawnSelect('selected-count', {
                count: selectedProviderRepoIds.length,
              })}
            </span>
            <div className="w-60">
              <SearchInput
                placeholder={spawnSelect('search-placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="max-h-90 overflow-y-auto rounded-lg border border-border-bg-neutral overflow-hidden bg-fill-interactive-neutral-idle">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border-bg-neutral">
              <Checkbox
                checked={workspaceCheckState}
                onCheckedChange={onWorkspaceToggle}
                onClick={(e) => e.stopPropagation()}
              />
              <Building2 className="size-4 shrink-0 text-muted-foreground" />
              <span className="font-medium text-sm grow">{workspaceName}</span>
              <button
                type="button"
                aria-label={
                  isWorkspaceExpanded
                    ? 'Collapse workspace'
                    : 'Expand workspace'
                }
                onClick={() => setIsWorkspaceExpanded((prev) => !prev)}
                className="text-muted-foreground hover:text-foreground"
              >
                <ChevronIcon className="size-4" />
              </button>
            </div>

            {isWorkspaceExpanded &&
              !isLoadingRepos &&
              filteredRepositories.map((repo, repoIndex) => {
                const isSelected = selectedProviderRepoIds.includes(
                  repo.providerRepoId,
                );
                const isLastRepo =
                  repoIndex === filteredRepositories.length - 1;

                return (
                  <label
                    key={repo.providerRepoId}
                    className={cn(
                      'flex items-center gap-3 pl-8 pr-4 py-2 cursor-pointer hover:bg-fill-panel-neutral',
                      !isLastRepo && 'border-b border-border-bg-neutral',
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() =>
                        onRepositoryToggle(repo.providerRepoId)
                      }
                    />
                    <Database className="size-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm">{repo.name}</span>
                  </label>
                );
              })}
          </div>
        </div>
      </Form>
    </Step>
  );
};
